import _ from 'lodash';

const orderByIdArr = (dataArr, indexArr) => {
    if(!indexArr) { return dataArr; }

    return _.sortBy(dataArr, ({id}) =>  {
        return (indexArr.indexOf(id)+1)*-1;
    })
};

/**
 * An instance of the question presenter is responsible for calculating the currently visible question.
 * It is given the quiz variable (contains all questions, question categories and potential answers) and the quizProgress, which holds all given answers from the participant.
 * When an answer to the quiz is submitted, it is immidiatly stored in the database so the progress of unfinished quizes is not lost.
 * If the applicant starts the quiz again, questions where the participant has already given an answer, are excluded from the questions.
 *
 * Questions are also grouped into their respective difficulty levels.
 * Only when the category.threshhold percent of questions are answered correctly, the particpant can see the questions from the next difficulty level.
 *
 * This allows selective showing of questions:
 * A participant can be asked if they know Technology X where yes is the right answer and no is the wrong answer.
 * Only when he answers this question with yes, the questions for this technology are shown.
 * Further, only when he has shown his proficiency with easy questions, difficult questions can be shown in the next level.
 */

export default class QuestionPresenter {
    /**
     * see setData
     * @param quiz
     * @param quizProgress
     */
    constructor(quiz, quizProgress) {
        this.setData(quiz, quizProgress);
    }

    /**
     * Set quiz and quizProgress instance variables and transform the quiz and quizProgress to a easier manageable form for later processing and recompute current data
     *
     *
     * @param quiz
     * example:
     * {
     *     categories: [
     *         {
     *            "Questions":[
     *               "recCXjWenvBkkHlTC",
     *               ...more question ids
     *            ],
     *            "threshhold":60, // if treshhold % of questions are answered correctly you advance to the next level - e.G 5 questions, 3 correct -> advance, only 2 correct not advance
     *            "sampleSize":3, //minimum sampled questions before advancing to the next level is possible
     *            "id":"recqtZdPczEGOCFlx",
     *            ...
     *         }
     *         ...
     *     ],
     *
     *     questions: [
     *         {
     *            "question":"Are you familiar with Javascript and ES6?",
     *            "Categories":[
     *               "recqtZdPczEGOCFlx"
     *            ],
     *            "Difficulty":1,
     *            "possibleAnswers":[
     *               "recaKcSP0NUXX5e8s",
     *               "reclYyyXIJuk6jpUU",
     *               ... possibly more answer ids
     *            ],
     *            "correctAnswers":[
     *               "recaKcSP0NUXX5e8s"
     *               ... possibly more correct answer ids
     *            ],
     *            "id":"recqzvVrAiHBpELN1",
     *            ...
     *         },
     *         ...lots of questions
     *     ],
     *
     *     possibleAnswers: [
     *         {
     *               "Name":"Yes",
     *               "id":"recaKcSP0NUXX5e8s"
     *         },
     *         ... lots of possible answers
     *     ],
     *     ...
     * }
     *
     * @param quizProgress
     * example:
     * {
     *     givenAnswers: [
     *          {
     *              choiceAnswers: ['recaKcSP0NUXX5e8s'] // answer ids
     *          }
     *     ]
     *
     * }
     */
    setData(quiz, quizProgress) {
        // set original data in the instance


        this.quiz = { ...quiz, categories: orderByIdArr(quiz.categories, quiz.quiz.Categories) };

        this.quizProgress = quizProgress;

        // initialize normalized question array
        this.questions = [];

        // initialize the question index
        this.questionIndex = {};

        this.categoryQuestions = {};

        // given a questionId return a function that returns true for objects with this particular questionId
        let createQuestionIdFilter = (questionId) => rel => rel.question?.indexOf(questionId) > -1;

        for (let question of this.quiz.questions) {
            let questionFilter = createQuestionIdFilter(question.id);

            // out of all possible answers get the possible answers for question
            let possibleAnswers = this.quiz.possibleAnswers.filter(questionFilter);
            possibleAnswers = orderByIdArr(possibleAnswers, question.possibleAnswers);

            // out of all given answers from the participant get the given answers for question
            let givenAnswers = this.quizProgress.givenAnswers.filter(questionFilter);

            // create question object that contains all the relevant data for further processing
            question = {
                ...question,
                possibleAnswers,
                givenAnswers
            };

            // add normalized question to array
            this.questions.push(question);

            // add normalized question to index
            this.questionIndex[question.id] = question;
        }

        for(let category of this.quiz.categories) {
            let unsortedQuestions  = this.questions.filter(q => q.Categories.indexOf(category.id) > -1);
            this.categoryQuestions[category.id] = orderByIdArr(unsortedQuestions, category.Questions);
        }

        // recompute according to new data
        this.currentData = this.computeCurrentData();
    }


    /**
     * get question by id
     *
     * @param id
     * @returns {*}
     */
    getQuestionById(id) {
        return this.questionIndex[id];
    }

    /**
     * get an array of all questions for a category identified by categoryId
     *
     * @param categoryId
     * @returns {*[]}
     */
    getCategoryQuestions(categoryId) {
        return this.categoryQuestions[categoryId];
    }

    /**
     * check if a question identified by questionId was answered by the participant
     *
     * @param questionId
     * @returns {boolean}
     */
    isQuestionAnswered(questionId) {
        return this.getQuestionById(questionId)
            .givenAnswers
            .length > 0;
    }

    /**
     * check if a question identified by questionId was answered successfully by the participant
     * @param questionId
     * @returns {boolean}
     */
    isQuestionAnsweredCorrectly(questionId) {
        let question = this.getQuestionById(questionId);

        // 
        let choiceIds = question.givenAnswers[0].choiceAnswers;
        let intersec = _.intersection(choiceIds, question.correctAnswers);
        return question.correctAnswers.length === intersec.length;

    }

    /**
     * get current question for given category data
     *
     * @param category category data object
     * @returns {*}
     */
    computeCategoryQuestion(category) {
        let { threshhold, sampleSize } = category,
            categoryQuestions = this.getCategoryQuestions(category.id),
            levels = _.groupBy(categoryQuestions, 'Difficulty'),
            minDifficulty = _.min(categoryQuestions.map(q => q.Difficulty)),
            maxDifficulty = _.max(categoryQuestions.map(q => q.Difficulty));


        for(let i = minDifficulty; i <= maxDifficulty; i++) {
            // console.log('iterating difficulty level', i);

            if (!levels.hasOwnProperty(i)) { continue; }
            let questions = levels[i];
            if(!_.isArray(questions)) { throw new Error('Invalid level '+i); }

            let answeredCorrectly = 0,
                objectiveQuestionCount = 0,
                levelSampleSize = Math.min(sampleSize, questions.length),
                percentageAnsweredCorrectly = 0;

            for(let j = 0; j < questions.length; j++) {
                let question = questions[j];

                if(!this.isQuestionAnswered(question.id)) {
                    return question;
                }

                if(!question.correctAnswers) {
                    continue;
                }

                objectiveQuestionCount++;
                if(!this.isQuestionAnsweredCorrectly(question.id)) {
                    continue;
                }

                answeredCorrectly++;
                percentageAnsweredCorrectly = (answeredCorrectly / objectiveQuestionCount) * 100;

                if(i < maxDifficulty && levelSampleSize <= objectiveQuestionCount) {
                    if(percentageAnsweredCorrectly >= threshhold) { break; }
                }
            }

            if(threshhold > percentageAnsweredCorrectly) {
                return null;
            }
        }

        return null;
    }

    computeCurrentData() {
        try {
            let {categories} = this.quiz;
            let categoriesDone = [];

            for(let i = 0; i < categories.length; i++) {
                let category = categories[i];

                categoriesDone.push(category.id);

                let question = this.computeCategoryQuestion(category);
                if(question) { return { category, question, categoriesDone }; }
            }

            return {};
        } catch(e) {
            throw e;
        }
    }

    getCurrentData() {
        return this.currentData;
    }
}
